import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChatMessage } from "@/api/models";
import { marked, type Tokens } from "marked";

/** Fallback dot pitch (button height + flex gap) used before the rail is measured. */
const FALLBACK_ITEM_PITCH = 14;
/** Breathing room kept at the top and bottom of the rail. */
const RAIL_PADDING = 8;
/** Never window below this many bars, even in a very short viewport. */
const MIN_VISIBLE_DOTS = 3;

type MarkdownToken = ReturnType<typeof marked.lexer>[number];

const markdownToPlainText = (content: string): string => {
  const getTokenText = (tokens: MarkdownToken[], separator = ""): string => tokens.map(token => {
    switch (token.type) {
      case "space":
      case "hr":
      case "br":
        return "\n";
      case "list":
        return (token as Tokens.List).items
          .map(item => getTokenText(item.tokens ?? [], "\n"))
          .join("\n");
      case "table":
        return [
          (token as Tokens.Table).header,
          ...(token as Tokens.Table).rows,
        ]
          .map(row => row.map(cell => getTokenText(cell.tokens ?? [])).join(" "))
          .join("\n");
      case "blockquote":
        return getTokenText(token.tokens ?? [], "\n");
      case "heading":
      case "paragraph":
        return getTokenText(token.tokens ?? []);
      case "text":
        return token.tokens ? getTokenText(token.tokens) : token.text;
      case "code":
      case "codespan":
      case "escape":
      case "html":
      case "image":
        return token.text;
      default:
        if ("tokens" in token && token.tokens) return getTokenText(token.tokens);
        return "text" in token ? token.text : "";
    }
  }).join(separator);

  return getTokenText(marked.lexer(content), "\n")
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
};

const getPreview = (content: string, wordLimit = 24): string => {
  const words = markdownToPlainText(content).replace(/\s+/g, " ").trim().split(" ");
  const snippet = words.slice(0, wordLimit).join(" ");
  return words.length > wordLimit ? snippet + "…" : snippet;
};

interface ChatNavMessage {
  message: ChatMessage;
  promptPreview: string;
  responsePreview: string;
}

interface ChatScrollNavProps {
  messages: ChatMessage[];
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  showSenderName?: boolean;
}

const ChatScrollNav = ({ messages, scrollContainerRef, showSenderName = false }: ChatScrollNavProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [maxVisible, setMaxVisible] = useState<number>(Number.POSITIVE_INFINITY);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [isPaneWide, setIsPaneWide] = useState(false);
  const rafRef = useRef<number | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);

  const navMessages = useMemo<ChatNavMessage[]>(() => {
    const turns: { message: ChatMessage; response: string }[] = [];

    messages.forEach(message => {
      if (message.role === "user") {
        turns.push({ message, response: "" });
      } else if (message.role === "assistant" && turns.length > 0 && message.content?.trim()) {
        // Keep the latest assistant message before the next user prompt as the turn preview.
        turns[turns.length - 1].response = message.content;
      }
    });

    return turns.map(({ message, response }) => ({
      message,
      promptPreview: getPreview(message.content ?? ""),
      responsePreview: getPreview(response, 36),
    }));
  }, [messages]);

  const updateActive = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || navMessages.length === 0) return;

    const containerTop = container.getBoundingClientRect().top;
    let activeId: string | null = null;
    let largestVisibleTop = -Infinity;
    let fallbackId: string | null = null;
    let largestNegativeTop = -Infinity;
    const containerHeight = container.clientHeight;

    navMessages.forEach(({ message: { id } }) => {
      if (!id) return;
      const el = container.querySelector<HTMLElement>(`[data-user-message-id="${id}"]`);
      if (!el) return;
      const top = el.getBoundingClientRect().top - containerTop;
      if (top >= 0 && top < containerHeight) {
        if (top > largestVisibleTop) {
          largestVisibleTop = top;
          activeId = id;
        }
      } else if (top < 0 && top > largestNegativeTop) {
        largestNegativeTop = top;
        fallbackId = id;
      }
    });

    setActiveId(activeId ?? fallbackId ?? (navMessages[0]?.message.id ?? null));
  }, [navMessages, scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateActive);
    };

    updateActive();
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [updateActive]);

  /**
   * The rail is absolutely positioned, so dots that don't fit would overflow it and
   * extend the page instead of being clipped. Measure how many dots the rail can hold
   * and window the list around the active dot rather than rendering all of them.
  **/
  useLayoutEffect(() => {
    const rail = railRef.current;
    const pane = rail?.parentElement;
    if (!rail || !pane) return;

    const measure = () => {
      const item = rail.querySelector<HTMLElement>("[data-nav-dot]");
      const pitch = item ? item.offsetHeight + 2 : FALLBACK_ITEM_PITCH; // + gap-0.5
      const available = rail.clientHeight - RAIL_PADDING * 2;
      setMaxVisible(Math.max(MIN_VISIBLE_DOTS, Math.floor(available / pitch)));
      setIsPaneWide(pane.clientWidth > 1148);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(rail);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [navMessages.length]);

  const { visibleMessages, hiddenAbove, hiddenBelow } = useMemo(() => {
    if (navMessages.length <= maxVisible) {
      return { visibleMessages: navMessages, hiddenAbove: 0, hiddenBelow: 0 };
    }
    const activeIndex = Math.max(0, navMessages.findIndex(({ message }) => message.id === activeId));
    const start = Math.min(
      Math.max(0, activeIndex - Math.floor(maxVisible / 2)),
      navMessages.length - maxVisible
    );
    return {
      visibleMessages: navMessages.slice(start, start + maxVisible),
      hiddenAbove: start,
      hiddenBelow: navMessages.length - start - maxVisible,
    };
  }, [navMessages, maxVisible, activeId]);

  const interactionIndex = hoveredIndex ?? focusedIndex;

  const scrollTo = (id: string) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLElement>(`[data-user-message-id="${id}"]`);
    if (!el) return;
    const containerTop = container.getBoundingClientRect().top;
    const elTop = el.getBoundingClientRect().top - containerTop;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    container.scrollBy({ top: elTop - 8, behavior: prefersReducedMotion ? "auto" : "smooth" });
  };

  if (navMessages.length < 2) return null;

  return (
    <nav
      ref={railRef}
      aria-label="Conversation message navigation"
      onMouseLeave={() => setHoveredIndex(null)}
      className={`${isPaneWide ? "flex" : "hidden"} flex-col items-start justify-center absolute left-2 top-0 bottom-0 z-10 pointer-events-none`}
    >
      {visibleMessages.map(({ message: m, promptPreview, responsePreview }, i) => {
        const preview = promptPreview;
        const isActive = activeId === m.id;
        const index = hiddenAbove + i;
        const distance = interactionIndex === null ? Number.POSITIVE_INFINITY : Math.abs(index - interactionIndex);
        const barWidth = distance === 0 ? 34 : distance === 1 ? 26 : distance === 2 ? 20 : isActive ? 18 : 12;
        const isInteracting = index === interactionIndex;
        const isEdge = (i === 0 && hiddenAbove > 0) || (i === visibleMessages.length - 1 && hiddenBelow > 0);
        return (
          <div key={m.id} className="group/item relative flex items-center pointer-events-auto">
            <button
              data-nav-dot
              onClick={() => m.id && scrollTo(m.id)}
              onMouseEnter={() => setHoveredIndex(index)}
              onFocus={() => {
                setHoveredIndex(null);
                setFocusedIndex(index);
              }}
              onBlur={() => setFocusedIndex(null)}
              aria-label={`Jump to message ${hiddenAbove + i + 1}: ${preview}`}
              aria-current={isActive ? "location" : undefined}
              className="h-3 w-[50px] px-2 cursor-pointer flex items-center justify-start bg-transparent border-none rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-base-100"
            >
              <span
                data-nav-bar
                style={{ transform: `scaleX(${barWidth / 34})` }}
                className={`block h-[3px] w-[34px] shrink-0 origin-left rounded-full transition-[transform,background-color,opacity] duration-300 ease-out motion-reduce:transition-none ${
                  distance === 0
                    ? "bg-base-content opacity-100"
                    : isActive
                      ? "bg-base-content/80 opacity-90"
                      : isEdge
                        ? "bg-base-content/20 opacity-80"
                        : "bg-base-content/30 opacity-90"
                }`}
              />
            </button>

            {/* Prompt and response preview, shown on hover or keyboard focus. */}
            <div
              className={`absolute left-full top-1/2 z-20 -translate-y-1/2 pointer-events-auto transition-[opacity,transform,visibility] duration-150 ease-out motion-reduce:transition-none ${
                isInteracting ? "visible translate-x-0 opacity-100" : "invisible translate-x-1 opacity-0"
              }`}
            >
              <div aria-hidden="true" className="w-[320px] max-w-[calc(100vw-5rem)] rounded-xl border border-base-content/10 bg-base-200/95 px-3.5 py-2.5 shadow-xl backdrop-blur-sm">
                {showSenderName && m.senderName && (
                  <p className="mb-1 text-[10px] font-semibold uppercase leading-none tracking-wide text-base-content/55 truncate">{m.senderName}</p>
                )}
                {preview && (
                  <p className="text-xs font-semibold leading-snug text-base-content truncate">{preview}</p>
                )}
                {responsePreview && (
                  <p
                    className="mt-1 overflow-hidden text-xs leading-relaxed text-base-content/60"
                    style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3 }}
                  >
                    {responsePreview}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
};

export default ChatScrollNav;
